import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ServiceMasterActions } from "@/components/common/section-components/action/ServiceMasterActions";
import { ServiceMasterFilter } from "@/components/common/section-components/filters/ServiceMasterFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { GetServicesListProps } from "@/services/service-master.service";
import { isEmpty } from "@/utils/query-string";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearFiltersHandler,
  fetchServiceMasterHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { serviceMasterHeaderElements } from "./schema/header-elements";
import {
  ServiceListEntity,
  ServicesListResponse,
  serviceMasterFilterSchema,
} from "./schema/service-master.schema";

export const ServiceMasterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPage = (location.state as { page?: number } | undefined)?.page ?? 1;

  const [isActive, setIsActive] = useState(false);

  const [loadingServices, setLoadingServices] = useState(false);
  const [services, setServices] = useState<ServicesListResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetServicesListProps> | undefined
  >();

  const { fetchPermissions, hasWriteAccess } = useRoles();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(serviceMasterFilterSchema),
    defaultValues: {
      Nombre: "",
      Categoria: [],
      UnidadCotizacion: [],
      Activo: [],
    },
  });

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchServices = async (
    filters?: Partial<GetServicesListProps>,
    showLoad = true,
  ) => {
    await fetchServiceMasterHandler({
      setLoadingServices,
      setServices,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      setPage,
      fetchServices,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      getValues,
      setOpenFilters,
      submitFilters: async () =>
        submitFiltersHandler({
          event: getValues(),
          setOpenFilters,
          setFiltersApplied,
          setCurrentFilters,
          setPage,
          fetchServices,
        }),
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchServices({ "P.Pagina": initialPage });
    }
  }, [isActive]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchServices({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.SERVICE_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: ServiceListEntity) => (
    <ServiceMasterActions handleDetail={handleDetail} item={item} />
  );

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const items =
    services?.listado?.map((el: ServiceListEntity) => ({
      ...el,
      activo: el.activo ? "Activo" : "Desactivado",
    })) ?? [];

  const handleAddService = () => {
    navigate(AppRoutes.SERVICE_CREATION);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="service-master-page">
      <SectionHeader
        title={"Maestro de servicios"}
        addButtonLabel="Agregar"
        handleAdd={handleAddService}
        loading={loadingServices}
        showButtons={hasWriteAccess(PermissionCode.SERVICES)}
      />

      <ServiceMasterFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingServices}
        loadingFilters={loadingServices}
        filtersApplied={filtersApplied}
        disableClear={loadingServices}
        disableApply={loadingServices || filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingServices && !services?.listado?.length) && <NoData />}

        {Boolean(!loadingServices && services?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              items={items}
              headers={serviceMasterHeaderElements}
              actionElements={getActionElements}
              noActions={!hasWriteAccess(PermissionCode.SERVICES)}
              noSelection
            />

            {Boolean(services?.totalPages && services.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={services?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
